import {ReactElement, Fragment} from "react"
import {AllDataDone} from "./alldata"
import {ISODate} from "./date"
import {Link} from "react-router-dom";
import utils from "./utils"
import {tuple} from "./tuple"
import {SOStatus, WARN_IF_VALUE_ABOVE} from "./api/salesorder"
import ExactOnlineLink from "./ExactOnlineLink"

type ErrorAndWarning<T extends "error" | "warning"> = {
  level: T
  name: string
  count: number
  formatted: ReactElement
  extra_info: ReactElement
}


export type ADError = ErrorAndWarning<"error">
export type ADWarning = ErrorAndWarning<"warning">

type Check = (allData: AllDataDone) => ADError | ADWarning
type ErrorCheck = (allData: AllDataDone) => ADError
type WarningCheck = (allData: AllDataDone) => ADWarning

const nonNegativeStock: ErrorCheck = allData => {
  /**
   * Checks that all stock is above > and an integer
   */
  const negativeStock = allData.projectedStocks.filter(
    ps => ps.currentRealStock < 0)
  return {
    level: "error",
    name: "Non-negative stock",
    count: negativeStock.length,
    formatted: <ul className="alldata-error sensible-stock-error">
      {negativeStock.map(
      ps => <li key={`${ps.warehouseCode} - ${ps.itemCode}`}>
        <Link to={[
            "",
            "division",
            allData.division.toString(),
            "PASDetailed",
            ps.warehouseCode,
            ps.itemCode,
          ].map(encodeURIComponent).join("/")}>
            {ps.warehouseCode} - {ps.itemCode}: {ps.currentRealStock}
        </Link>
      </li>)}
    </ul>,
    extra_info:
      <p>
        The current real in-warehouse stock for some items is negative.
        Obviously it's not possible to have a negative number of items in stock,
        so this has to be fixed. Either adjust the stock for these items, or if
        these items are not to be considered by this app, then switch off the
        "stock", "sales" or "purchase" checkboxes in Exact.
      </p>
  }
}


const noPartiallyFulfilledSalesOrderLines: ErrorCheck = allData => {
  /**
   * Checks that all stock is above > and an integer
   */
  const partiallyFulfilledSalesOrderLines = allData.salesOrderLines
    .filter(sol => sol.quantityDelivered !== 0 && (
      sol.quantityDelivered < sol.quantity ||
      sol.deliveryStatus !== SOStatus.CLOSED))
  const salesOrdersWithPartialLines = utils.intoBuckets(
    partiallyFulfilledSalesOrderLines, sol => sol.parent)
  return {
    level: "error",
    name: "No Partially fulfilled sales order lines",
    count: salesOrdersWithPartialLines.size,
    formatted:
      <ul className="alldata-warning no-partially-fulfilled-sales-order-lines">
        {[...salesOrdersWithPartialLines.entries()].map(([so, sols]) =>
        <li key={so.orderNumber}>
          <a href={so.getExactOnlineUrl(allData.division).toString()}
            target="_blank" rel="noreferrer">
            Sales order {so.orderNumber} (
            {so.deliverToName}; {so.description})
          </a>. Order lines: <ul>{
            [...sols].sort((a, b) => a.lineNumber - b.lineNumber).map(
            sol => <li key={sol.iD}>
              {sol.lineNumber}: delivered {sol.quantityDelivered} out
              of {sol.quantity} {sol.itemCode}.
            </li>
            )}
          </ul>
        </li>)}
      </ul>,
    extra_info:
      <>
        <p>
          The Nose CC app assumes that a sales order line is either 0% delivered, or
          100% delivered. Lines that have a subset of items delivered lead to lots
          of unwanted complexity in the SOtool (for instance, what is the delivery
          date for such a line?). Therefore running the SOtool while there are
          lines that are not either 0% or 100% fulfilled is not supported and
          will lead to errors.
        </p>
        <p>
          If somehow you want to ship part of an order line, split the line in two
          so that you can ship the whole order.
        </p>
      </>
  }
}

const integerStock: ErrorCheck = allData => {
  /**
   * Checks that all stock is an integer
   */
  const nonIntegerStock = allData.projectedStocks.filter(
    ps => Math.round(ps.currentRealStock) !== ps.currentRealStock)
  return {
    level: "error",
    name: "Non-fractional stock",
    count: nonIntegerStock.length,
    formatted: <ul className="alldata-error integer-stock-error">
      {nonIntegerStock.map(
      ps => <li key={`${ps.warehouseCode} - ${ps.itemCode}`}>
        <Link to={[
            "",
            "division",
            allData.division.toString(),
            "PASDetailed",
            ps.warehouseCode,
            ps.itemCode,
          ].map(encodeURIComponent).join("/")}>
            {ps.warehouseCode} - {ps.itemCode}: {ps.currentRealStock}
        </Link>
      </li>)}
    </ul>,
    extra_info:
      <p>
        All stock should be recorded in whole items (so having half an item in
        stock is not allowed).
        In the future it's possible that support for non-whole-number stock
        will be added (however at the moment it's not).
      </p>
  }
}


const positiveProjectedStockCheck: WarningCheck = allData => {
  /**
   * Checks that the projected REAL stock never gets negative.
   * Note that projected available stock can only get negative if projected
   * real stock becomes negative.
   *
   * Note that items with a negative current real stock are filtered out since
   * they are already part of an error.
   */
  const negativeProjectedStocks = allData.projectedStocks.filter(
    ps => ps.currentRealStock >= 0 && [
      ...ps.projection.values()].some(proj => proj.eodProjectedRealStock < 0))
  return {
    level: "warning",
    name: "No negative stock projected",
    count: negativeProjectedStocks.length,
    formatted: <dl className="alldata-warning positive-projected-stock-warning">
      {negativeProjectedStocks.map(ps =>
        <Fragment key={`${ps.warehouseCode} - ${ps.itemCode}`}>
          <dt><Link to={[
            "",
            "division",
            allData.division.toString(),
            "PASDetailed",
            ps.warehouseCode,
            ps.itemCode,
          ].map(encodeURIComponent).join("/")}>
            {ps.warehouseCode} - {ps.itemCode}
          </Link></dt>
          <dd>Negative stock during: {(() => {
            let negative = false
            let result = new Array<string>()
            if (ps.currentRealStock < 0) {
              negative = true
              result.push("today\u2013")
            }
            ps.projection.forEach((proj, key) => {
              if (proj.eodProjectedRealStock < 0 && !negative) {
                negative = true
                result.push(key.toDDMMYYYYString() + "\u2013")
              } else if (proj.eodProjectedRealStock >= 0 && negative) {
                negative = false
                result[result.length - 1] += key.toDDMMYYYYString()
              }
            })
            if (negative) {
                result[result.length - 1] += "end of time"
            }
            return result.join(", ")
          })()}</dd>
          </Fragment>)
      }
    </dl>,
    extra_info:
      <>
      <p>
        Good stock management means that you should never have your projected
        real stock go negative. This happens when you plan to ship more items
        in the future than you actually are projected to have in stock at that time.
      </p>
      <p>
        This warning lists those periods when projected <em>real</em> stock is
        negative. Projected <em>available</em> stock is most likely negative
        for a much longer time.
      </p>
      <p>
        In some cases, running the POtool can fix this issue (it will move some
        shipments backwards), however if there is no enddate for the negative
        stock, it means you should order new stock.
      </p>
      <p>
        Note that items with current negative stock are already filtered out
        in this warning, since they appear in an error.
      </p>
      </>
  }
}

const noItemWarehousePlanningDetails_sInThePast: WarningCheck = allData => {
  const TODAY = ISODate.localToday()
  const itemWarehousePlanningDetailsInThePast =
    allData.itemWarehousePlanningDetails_s.filter(
      iwpd => iwpd.plannedDate.lt(TODAY))
  const bySourceParent = utils.intoBuckets(
    itemWarehousePlanningDetailsInThePast, iwpd => iwpd.planningSource.parent)
  return {
    level: "warning",
    name: "Unfulfilled transactions with dates in the past",
    count: bySourceParent.size,
    formatted:
      <ul
        className="alldata-warning no-item-warehouse-planning-details-in-the-past">
        {[...bySourceParent.entries()].map(([parent, iwpds]) =>
        <li key={parent.getId()}>
          <ExactOnlineLink item={parent} division={allData.division} long />;
          Lines: <ul>{
            [...iwpds]
            .sort(
              (a, b) => a.planningSource.lineNumber - b.planningSource.lineNumber)
            .map(iwpd => <li key={iwpd.getUniqueKey()}>
              {iwpd.planningSource.lineNumber}: {iwpd.planningSource.quantity} {
              iwpd.planningSource.itemCode}; {iwpd.plannedDate.toDDMMYYYYString()}
            </li>
            )}
          </ul>
        </li>)}
      </ul>,
    extra_info:
      <>
        <p>
          Order lines with delivery dates in the past should either have been
          shipped/received already, or the date should be changed to when they
          actually ship/arrive.
        </p>
        <p>
          In case of Sales Orders, running the SO tool
          should update the delivery date for these lines.
        </p>
      </>
  }
}


const notTooExpensiveSalesOrderLinesCheck: WarningCheck = allData => {
  /**
   * Checks that no salesorder line has an amountDC of more than 10k
   */
  const tooExpensiveLines = allData.salesOrderLines.filter(
    sol => sol.$calc.warnIfTooLarge && sol.amountDC > WARN_IF_VALUE_ABOVE)
  return {
    level: "warning",
    name: "Large sales order lines are split",
    count: tooExpensiveLines.length,
    formatted: <ul className="alldata-warning too-expensive-sales-order-lines-check">
      {tooExpensiveLines.map(sol =>
        <Fragment key={sol.iD}>
          <li>
            <a href={sol.parent.getExactOnlineUrl(allData.division).toString()}
              target="_blank" rel="noreferrer">
              Sales order {sol.parent.orderNumber} (
              {sol.parent.deliverToName}; {sol.parent.description})
            </a> (order line {sol.lineNumber}): {sol.quantity} {sol.itemCode} (
            {sol.itemDescription}): <strong>&euro;{sol.amountDC}</strong>
            <button className="ignore-line-forever live"
              onClick={() => sol.update({"$calc.warnIfTooLarge": false},
              allData.division, "ignore button", allData.tick)}>
              ignore line forever
            </button>
          </li>
        </Fragment>)}
    </ul>,
    extra_info:
      <>
        <p>
          Large sales order lines run a risk of being shipped later (when the whole
          order quantity is available), even though it would have been possible to
          ship sub-orders earlier.
        </p>
        <p>
          It is therefore advised to split large order-lines, so that individual
          sublines get allocated earlier (and can be sent as part of a partial
          shipment).
        </p>
        <p>
          Note that it's possible to silence this warning by
          adding <code>&gt;@@warn-if-too-large: no</code> to the sales order line.
        </p>
      </>
  }
}

const noNoteFieldParsingErrors: ErrorCheck = allData => {
  /**
   * Checks that the "errors" array is empty on all elements that define
   * calc-types
   */
  const errors: ReactElement[] = [
    ...allData._rawPurchaseOrders
    .flatMap(po => po.purchaseOrderLines)
    .flatMap(pol => pol.$calc.errors.map(
      error => <>
        <a href={pol.parent.getExactOnlineUrl(allData.division).toString()}
            target="_blank" rel="noreferrer">
          Purchase order {pol.parent.orderNumber}
        </a> line {pol.lineNumber}: {error}
      </>
    )),
    ...allData._rawSalesOrders
    .flatMap(so => so.$calc.errors.map(
      error => <>
        <a href={so.getExactOnlineUrl(allData.division).toString()}
            target="_blank" rel="noreferrer">
          Sales order {so.orderNumber} </a>: {error}
      </>
    )),
    ...allData._rawSalesOrders
    .flatMap(so => so.salesOrderLines)
    .flatMap(sol => sol.$calc.errors.map(
      error => <>
        <a href={sol.parent.getExactOnlineUrl(allData.division).toString()}
            target="_blank" rel="noreferrer">
          Sales order {sol.parent.orderNumber}
        </a> line {sol.lineNumber}: {error}
      </>
    )),

  ]
  return {
    level: "error",
    name: "Note fields are valid",
    count: errors.length,
    formatted: <ul>
      {errors.map((error, index) =><li key={index}>{error}</li>)}
    </ul>,
    extra_info: <>
      <p>
        Any errors in notes fields, means most likely that the field does not
        do what you expect. As a result this may lead to errors later in the SO
        tool. Please fix the places where mistakes were made.
      </p>
    </>
  }
}


const matchingDropShipments: ErrorCheck = allData => {
  /**
   * Checks that the POs and SOs with >@@drop-shipment set match up
   */
  const polLookup = new Map(
    allData._rawPurchaseOrders
    .flatMap(po => po.purchaseOrderLines)
    .map(pol => [tuple([pol.parent.orderNumber, pol.lineNumber]), pol]))
  const solLookup = new Map(
    allData._rawSalesOrders
    .flatMap(so => so.salesOrderLines)
    .map(sol => [tuple([sol.parent.orderNumber, sol.lineNumber]), sol]))

  const errors: ReactElement[] = [
    ...allData._rawPurchaseOrders
    .flatMap(po => po.purchaseOrderLines)
    .filter(pol => pol.$calc.dropShipment !== null)
    .map(pol => {
      const [soNr, solLineNr] = pol.$calc.dropShipment as [number, number]
      const sol = solLookup.get(tuple([soNr, solLineNr]))
      let errorstart = <>
        <a href={pol.parent.getExactOnlineUrl(allData.division).toString()}
            target="_blank" rel="noreferrer">
          Purchase order {pol.parent.orderNumber}
        </a> line {pol.lineNumber}:
      </>
      if (pol.$calc.poRedirect) {
        return <>{errorstart} defined both a dropShipment and a poRedirect
        (meaning a shipment to another warehouse). This cannot be happening at the
        same time.</>
      }
      if (!sol) {
        return <>{errorstart} linked sales order {soNr} line {solLineNr} doesn't
        exist</>
      }
      errorstart = <>{errorstart} linked <a
        href={sol.parent.getExactOnlineUrl(allData.division).toString()}
            target="_blank" rel="noreferrer">
          Sales order {sol.parent.orderNumber}
        </a> line {sol.lineNumber} </>
      if (!sol.$calc.dropShipment) {
        return <>{errorstart} does not
        have a <code>&gt;@@drop-shipment:</code> tag</>
      }
      if (sol.$calc.dropShipment[0] !== pol.parent.orderNumber ||
          sol.$calc.dropShipment[1] !== pol.lineNumber) {
        return <>{errorstart} points to a different purchase order line:
          PO:{sol.$calc.dropShipment[0]}({sol.$calc.dropShipment[1]})
        </>
      }

      if (pol.itemCode !== sol.itemCode) {
        return <>{errorstart} is for a different item</>
      }

      if ((pol.quantity - pol.receivedQuantity) !==
          (sol.quantity - sol.quantityDelivered)) {
        return <>{errorstart} has a different quantity to still be delivered</>
      }

      if (pol.receiptDate !== sol.deliveryDate) {
        return <>{errorstart} has a receiptdate / deliverydate</>
      }

      if (pol.parent.warehouseCode !== sol.parent.warehouseCode) {
        return <>{errorstart} is not for the same warehouse</>
      }
      return null
    })
    .filter(error => error) as ReactElement[],

    ...allData._rawSalesOrders
    .flatMap(so => so.salesOrderLines)
    .filter(sol => sol.$calc.dropShipment !== null)
    .map(sol => {
      const [poNr, polLineNr] = sol.$calc.dropShipment as [number, number]
      const pol = polLookup.get(tuple([poNr, polLineNr]))
      let errorstart = <>
        <a href={sol.parent.getExactOnlineUrl(allData.division).toString()}
            target="_blank" rel="noreferrer">
          Sales order {sol.parent.orderNumber}
        </a> line {sol.lineNumber}:
      </>
      if (!pol) {
        return <>{errorstart} linked purchase order {poNr} line {polLineNr} doesn't
        exist</>
      }
      errorstart = <>{errorstart} linked <a
        href={pol.parent.getExactOnlineUrl(allData.division).toString()}
            target="_blank" rel="noreferrer">
          Purchase order {pol.parent.orderNumber}
        </a> line {pol.lineNumber} </>
      if (!pol.$calc.dropShipment) {
        return <>{errorstart} does not
        have a <code>&gt;@@drop-shipment:</code> tag</>
      }
      if (pol.$calc.dropShipment[0] !== sol.parent.orderNumber ||
          pol.$calc.dropShipment[1] !== sol.lineNumber) {
        return <>{errorstart} points to a different sales order line:
          SO:{pol.$calc.dropShipment[0]}({pol.$calc.dropShipment[1]})
        </>
      }
      // other checks not necessary, already done in PO
      return null
    })
    .filter(error => error) as ReactElement[],
  ]
  return {
    level: "error",
    name: "Drop shipments PO line and SO line match",
    count: errors.length,
    formatted: <ul>
      {errors.map((error, index) =><li key={index}>{error}</li>)}
    </ul>,
    extra_info: <>
      <p>
        Through the <code>&gt;@@drop-shipment</code> tag, purchase order lines and
        sales order lines can be connected. It is <em>essential</em> that this
        match is 100% accurate, meaning that for every PO that is a drop shipment,
        there needs to be a SO that is also a drop shipment (and that points
        back to the PO). All data for the two lines have to be the same: the item
        code, the number of items still to be delivered, the date, and the
        warehouse.
      </p>
    </>
  }
}


const matchingPoRedirects: ErrorCheck = allData => {
  /**
   * Checks that the POs and WTs with >@@po-redirect set match up
   */
  const polLookup = new Map(
    allData._rawPurchaseOrders
    .flatMap(po => po.purchaseOrderLines)
    .map(pol => [tuple([pol.parent.orderNumber, pol.lineNumber]), pol]))
  const wtlLookup = new Map(
    allData._rawWarehouseTransfers
    .flatMap(wt => wt.warehouseTransferLines)
    .map(wtl => [tuple([wtl.parent.transferNumber, wtl.lineNumber]), wtl]))

  const errors: ReactElement[] = [
    ...allData._rawPurchaseOrders
    .flatMap(po => po.purchaseOrderLines)
    .filter(pol => pol.$calc.poRedirect !== null)
    .map(pol => {
      const [wtNr, wtlLineNr] = pol.$calc.poRedirect as [number, number]
      const wtl = wtlLookup.get(tuple([wtNr, wtlLineNr]))
      let errorstart = <>
        <a href={pol.parent.getExactOnlineUrl(allData.division).toString()}
            target="_blank" rel="noreferrer">
          Purchase order {pol.parent.orderNumber}
        </a> line {pol.lineNumber}:
      </>
      if (!wtl) {
        return <>{errorstart} linked warehouse transfer {wtNr} line {wtlLineNr}
          doesn't exist</>
      }
      errorstart = <>{errorstart} linked <a
        href={wtl.parent.getExactOnlineUrl(allData.division).toString()}
            target="_blank" rel="noreferrer">
          Warehouse transfer {wtl.parent.transferNumber}
        </a> line {wtl.lineNumber} </>
      if (!wtl.$calc.poRedirect) {
        return <>{errorstart} does not
        have a <code>&gt;@@po-redirect:</code> tag</>
      }
      if (wtl.$calc.poRedirect[0] !== pol.parent.orderNumber ||
          wtl.$calc.poRedirect[1] !== pol.lineNumber) {
        return <>{errorstart} points to a different purchase order line:
          PO:{wtl.$calc.poRedirect[0]}({wtl.$calc.poRedirect[1]})
        </>
      }

      if (pol.itemCode !== wtl.itemCode) {
        return <>{errorstart} is for a different item</>
      }

      if ((pol.quantity - pol.receivedQuantity) !== wtl.quantity) {
        return <>{errorstart} has a different quantity to still be delivered</>
      }

      if (pol.receiptDate !== wtl.parent.plannedDeliveryDate) {
        return <>{errorstart} mismatch between receiptdate on PO and
        plannedDeliveryDate on WT</>
      }

      if (pol.parent.warehouseCode !== wtl.parent.warehouseFromCode) {
        return <>{errorstart} is not for the same warehouse</>
      }
      return null
    })
    .filter(error => error) as ReactElement[],

    ...allData._rawWarehouseTransfers
    .flatMap(wt => wt.warehouseTransferLines)
    .filter(wtl => wtl.$calc.poRedirect !== null)
    .map(wtl => {
      const [poNr, polLineNr] = wtl.$calc.poRedirect as [number, number]
      const pol = polLookup.get(tuple([poNr, polLineNr]))
      let errorstart = <>
        <a href={wtl.parent.getExactOnlineUrl(allData.division).toString()}
            target="_blank" rel="noreferrer">
          Warehouse transfer {wtl.parent.transferNumber}
        </a> line {wtl.lineNumber}:
      </>
      if (!pol) {
        return <>{errorstart} linked purchase order {poNr} line {polLineNr} doesn't
        exist</>
      }
      errorstart = <>{errorstart} linked <a
        href={pol.parent.getExactOnlineUrl(allData.division).toString()}
            target="_blank" rel="noreferrer">
          Purchase order {pol.parent.orderNumber}
        </a> line {pol.lineNumber} </>
      if (!pol.$calc.poRedirect) {
        return <>{errorstart} does not
        have a <code>&gt;@@po-redirect:</code> tag</>
      }
      if (pol.$calc.poRedirect[0] !== wtl.parent.transferNumber ||
          pol.$calc.poRedirect[1] !== wtl.lineNumber) {
        return <>{errorstart} points to a different warehouse transfer line:
          SO:{pol.$calc.poRedirect[0]}({pol.$calc.poRedirect[1]})
        </>
      }
      // other checks not necessary, already done in PO
      return null
    })
    .filter(error => error) as ReactElement[],
  ]
  return {
    level: "error",
    name: "PO redirects have match PO lines and WT lines",
    count: errors.length,
    formatted: <ul>
      {errors.map((error, index) =><li key={index}>{error}</li>)}
    </ul>,
    extra_info: <>
      <p>
        Through the <code>&gt;@@po-redirect</code> tag, purchase order lines and
        warehouse transfer lines can be connected. It is <em>essential</em> that this
        match is 100% accurate, meaning that for every PO that actually will be 
        delivered to a different warehouse, there is exactly one Warehouse
        transfer line that points back, All data for the two lines have to be
        the same: the item
        code, the number of items still to be delivered, the date, and the
        warehouse.
      </p>
    </>
  }
}

const uniqueLineNumbers: WarningCheck = allData => {
  const polKeys = new Set()
  const polDuplicates = allData._rawPurchaseOrders
  .flatMap(po => po.purchaseOrderLines)
  .filter(pol => {
    const key = tuple([pol.parent.orderNumber, pol.lineNumber])
    if (polKeys.has(key)) {
      return true
    }
    polKeys.add(key)
    return false
  })

  const solKeys = new Set()
  const solDuplicates = allData._rawSalesOrders
  .flatMap(so => so.salesOrderLines)
  .filter(sol => {
    const key = tuple([sol.parent.orderNumber, sol.lineNumber])
    if (solKeys.has(key)) {
      return true
    }
    solKeys.add(key)
    return false
  })

  return {
    level: "warning",
    name: "Unique order numbers and line numbers",
    count: polDuplicates.length + solDuplicates.length,
    formatted:<ul>
      {polDuplicates.map((pol, index) =>
          <li key={index}>
            <a href={pol.parent.getExactOnlineUrl(allData.division).toString()}
              target="_blank" rel="noreferrer">
              Purchase order {pol.parent.orderNumber}
            </a> (line {pol.lineNumber})
          </li>)}
      {solDuplicates.map((sol, index) =>
          <li key={index}>
            <a href={sol.parent.getExactOnlineUrl(allData.division).toString()}
              target="_blank" rel="noreferrer">
              Sales order {sol.parent.orderNumber}
            </a> (line {sol.lineNumber})
          </li>)}
      </ul>,
    extra_info: <>
      <p>
        Normally all Purchase / Sales orders and Warehouse Transfers should have
        unique orderNumbers and line numbers.
        Sometimes through an error in Exact, this is not the case.
        The advise is to remove this order (or at least this order line) and re-add
        it again.
        If not, most stuff will work, but every now and then a small thing may
        break.
      </p>
    </>
  }
}





export const ALL_CHECKS: ReadonlyArray<Check> = [
  nonNegativeStock,
  noPartiallyFulfilledSalesOrderLines,
  integerStock,
  positiveProjectedStockCheck,
  notTooExpensiveSalesOrderLinesCheck,
  noItemWarehousePlanningDetails_sInThePast,
  noNoteFieldParsingErrors,
  matchingDropShipments,
  matchingPoRedirects,
  uniqueLineNumbers,
]
